import { Dialog, Dropdown, Icon, IDropdownOption, Separator, Stack, Text, TextField } from '@fluentui/react';
import { Button, buttonStylesDanger, ClipboardButton, useClassNames, useTheme } from '@h2oai/ui-kit';
import { useEffect, useState } from 'react';

import { IShareModal, IShareModalClassNames, shareModalStyle } from './ShareModal.style';

interface GetLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  shareUrl?: string;
  s3ReferenceUrl?: string;
  itemName?: string;
  isDirectory?: boolean;
  itemKey?: string;
  onGeneratePresignedUrl?: (key: string, expiresInSeconds: number) => Promise<string>;
}

const TTL_OPTIONS: IDropdownOption[] = [
  { key: 3600, text: '1 hour' },
  { key: 21600, text: '6 hours' },
  { key: 86400, text: '24 hours' },
  { key: 604800, text: '7 days' },
];

export const GetLinkModal = (props: GetLinkModalProps) => {
  const { isOpen, onClose, shareUrl, s3ReferenceUrl, itemName, itemKey, onGeneratePresignedUrl } = props;
  const [selectedTtl, setSelectedTtl] = useState<number>(3600);
  const [presignedUrl, setPresignedUrl] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [expirationTime, setExpirationTime] = useState<Date | null>(null);

  const theme = useTheme();
  const classNames = useClassNames<IShareModal, IShareModalClassNames>('ShareModal', shareModalStyle);
  const styles = shareModalStyle(theme);

  // Reset state when modal opens or when the share URLs change (e.g., workspace switch)
  useEffect(() => {
    if (isOpen) {
      setPresignedUrl('');
      setExpirationTime(null);
      setSelectedTtl(3600);
    }
  }, [isOpen, shareUrl, s3ReferenceUrl, itemKey]);

  const handleGeneratePresignedUrl = async () => {
    if (!itemKey || !onGeneratePresignedUrl) return;

    try {
      setIsGenerating(true);
      const url = await onGeneratePresignedUrl(itemKey, selectedTtl);
      setPresignedUrl(url);
      setExpirationTime(new Date(Date.now() + selectedTtl * 1000));
    } catch (error) {
      console.error('Failed to generate presigned URL:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const formatExpirationTime = (date: Date): string => {
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Dialog
      hidden={!isOpen}
      onDismiss={onClose}
      minWidth={600}
      dialogContentProps={{
        type: 0,
        title: `Get link for "${itemName}"`,
        closeButtonAriaLabel: 'Close',
        showCloseButton: true,
      }}
    >
      <Stack tokens={{ childrenGap: 12 }}>
        {/* S3 Reference Link Section */}
        {s3ReferenceUrl && (
          <Stack tokens={{ childrenGap: 12 }}>
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
              <Icon iconName="Cloud" className={classNames.s3ReferenceIcon} />
              <Text className={classNames.sectionHeader}>S3 Reference Link</Text>
            </Stack>

            <Stack className={classNames.sectionCard} tokens={{ childrenGap: 12 }}>
              <Text className={classNames.sectionDescription}>Direct S3 URI for programmatic access</Text>
              <Stack horizontal tokens={{ childrenGap: 8 }}>
                <TextField
                  value={s3ReferenceUrl}
                  readOnly
                  styles={styles.textFieldMonospace}
                  data-test="share-s3-reference-input"
                />
                <ClipboardButton content={s3ReferenceUrl} data-test="copy-s3-reference-button" />
              </Stack>
            </Stack>
          </Stack>
        )}

        {/* Pre-signed URL Section */}
        {onGeneratePresignedUrl && itemKey && (
          <>
            <Separator />
            <Stack tokens={{ childrenGap: 12 }}>
              <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                <Icon iconName="Clock" className={classNames.temporaryLinkIcon} />
                <Text className={classNames.sectionHeader}>Public Download Link</Text>
              </Stack>

              <Stack className={classNames.sectionCard} tokens={{ childrenGap: 12 }}>
                <Stack
                  horizontal
                  verticalAlign="center"
                  tokens={{ childrenGap: 8 }}
                  className={classNames.warningBanner}
                >
                  <Icon iconName="Warning" className={classNames.warningIcon} />
                  <Text className={classNames.warningText}>
                    This will create a public download link. It cannot be manually revoked and will remain active until
                    it expires.
                  </Text>
                </Stack>

                <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="end">
                  <Stack tokens={{ childrenGap: 8 }}>
                    <Text className={classNames.expirationLabel}>Expires In</Text>
                    <Dropdown
                      options={TTL_OPTIONS}
                      selectedKey={selectedTtl}
                      onChange={(_e, option) => option && setSelectedTtl(option.key as number)}
                      data-test="presigned-url-ttl-dropdown"
                    />
                  </Stack>
                  <Button
                    text="Generate Link"
                    onClick={handleGeneratePresignedUrl}
                    disabled={isGenerating}
                    styles={[buttonStylesDanger]}
                    data-test="generate-presigned-url-button"
                  />
                </Stack>

                {presignedUrl && (
                  <Stack tokens={{ childrenGap: 12 }}>
                    <Stack horizontal tokens={{ childrenGap: 8 }}>
                      <TextField
                        value={presignedUrl}
                        readOnly
                        styles={styles.textFieldMonospace}
                        data-test="presigned-url-input"
                      />
                      <ClipboardButton content={presignedUrl} data-test="copy-presigned-url-button" />
                    </Stack>
                    {expirationTime && (
                      <Stack
                        horizontal
                        verticalAlign="center"
                        tokens={{ childrenGap: 8 }}
                        className={classNames.expirationBanner}
                      >
                        <Icon iconName="Clock" className={classNames.expirationIcon} />
                        <Text className={classNames.expirationText} data-test="presigned-url-expiration">
                          Expires: {formatExpirationTime(expirationTime)}
                        </Text>
                      </Stack>
                    )}
                  </Stack>
                )}
              </Stack>
            </Stack>
          </>
        )}
      </Stack>
    </Dialog>
  );
};
